import Utils from '../../../Utils';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;

// Builds a local-time Date from <input type="date"> + <input type="time"> values.
// Returns null for anything that is not a real calendar date and time of day.
export const combineLocalDateTime = (date, time) => {
    const d = DATE_RE.exec(date || '');
    const t = TIME_RE.exec(time || '');
    if (!d || !t) return null;
    const [year, month, day, hours, minutes] = [d[1], d[2], d[3], t[1], t[2]].map(Number);
    const result = new Date(year, month - 1, day, hours, minutes);
    const isReal =
        result.getFullYear() === year &&
        result.getMonth() === month - 1 &&
        result.getDate() === day &&
        result.getHours() === hours &&
        result.getMinutes() === minutes;
    return isReal ? result : null;
};

export const splitLocalDateTime = value => {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return { date: '', time: '' };
    const [date, time] = Utils.dateToDatetimeFormat(d).split('T');
    return { date, time };
};

// Ordinary workouts are scheduled by a single start; the API derives/keeps any end.
export const buildWorkoutCreatePayload = (date, time) => {
    const start = combineLocalDateTime(date, time);
    return start ? { workoutStart: start.toISOString() } : null;
};

// Missing end => point event; an existing end (legacy / program / race) is kept as a span.
export const workoutToCalendarEvent = workout => {
    const start = new Date(workout.workoutStart);
    const storedEnd = workout.workoutEnd ? new Date(workout.workoutEnd) : null;
    const hasSpan = storedEnd && !Number.isNaN(storedEnd.getTime()) && storedEnd > start;
    return {
        ...workout,
        title: 'Swimming Workout',
        type: 'workout',
        start,
        end: hasSpan ? storedEnd : start,
        workoutStart: Utils.dateToDatetimeFormat(start),
        workoutEnd: hasSpan ? Utils.dateToDatetimeFormat(storedEnd) : ''
    };
};

// A race's raceDate is a date-only value persisted at UTC midnight, so read it with UTC
// getters: local getters would show the previous day in negative-offset timezones.
export const utcDateOnly = value => {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}-${month}-${day}`;
};

// Local-midnight Date for a date-only raceDate, so the calendar places it on the stored day.
const localDateFromUtcDateOnly = value => {
    const d = new Date(value);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// Timed races keep raceStart/raceEnd as real instants; legacy races are all-day on raceDate.
export const raceToCalendarEvent = event => ({
    ...event,
    title: `${event.raceType}m ${event.raceStyle}`,
    type: 'race',
    raceDate: utcDateOnly(event.raceDate),
    allDay: event.raceStart && event.raceEnd ? false : true,
    start: event.raceStart ? new Date(event.raceStart) : localDateFromUtcDateOnly(event.raceDate),
    end: event.raceEnd ? new Date(event.raceEnd) : localDateFromUtcDateOnly(event.raceDate),
    status: event.status || 'Assigned'
});
